// cSpell:ignore forfait kost ownerbase pand pand's panden Subtotaal Totaal Verdeling
// One pand's part of a cost spread over its tenants pro rata the days they
// occupied it in the cost's period, its vacancy and, for a shared cost, the
// owner's fixed share: the table of the cost popup's "Verdeling" step. The
// cost's drawer shows the same table, so a manager recognizes the split they
// saw when they entered the cost.

import { getLocalizedText } from "@rentiohq/shared-frontend/dist/utils/i18n/i18n.utils";
import { DataTable, TextStyle } from "@rentiohq/web-shared/dist/components";
import { FontWeights } from "@rentiohq/web-theme";
import React from "react";
import styled from "styled-components";
import { costDayText } from "../costPeriod.utils";
import { costRowsVat, TCostBilling } from "../costVat.utils";
import { prorateByOccupancy } from "../engine";
import { vatOfAmount } from "../money.utils";
import { vatLineText } from "../moneyText.utils";
import { ContractRef } from "../types";
import { Money, moneyFont, MoneyWithVat } from "./Money";

// A row under which the amount is too small to show (a fraction of a cent).
const OWNER_BASE_THRESHOLD = 0.005;

// The settlement summary's table look around the per-pand DataTable (Lewis,
// 2026-10-21): no frame and no gray bands, only a hairline between the rows.
const UnitDistTable = styled.div`
  table {
    width: 100%;
  }
  thead th {
    white-space: nowrap;
  }
  tbody td {
    border-bottom: 1px solid ${({ theme }) => theme.colors.neutral20};
    vertical-align: top;
  }
  tbody tr:last-child td {
    border-bottom: none;
  }

  /* The name column flexes; ratio + amount never wrap. */
  th:first-child,
  td:first-child {
    width: 50%;
  }
  td:nth-child(2),
  td:nth-child(3) {
    white-space: nowrap;
  }
  th:nth-child(2),
  td:nth-child(2) {
    text-align: center;
  }
`;

const UnitHeading = styled.div`
  font-size: 14px;
  font-weight: ${FontWeights.Bold};
  color: ${({ theme }) => theme.getColor("gray", 1000)};
  margin: 4px 0 8px;
`;

// The total under a pand's table, and under the panden together, as the
// summary's: a line, then the label right-aligned next to its amount.
export const DistTotal = styled.div`
  display: flex;
  align-items: baseline;
  justify-content: flex-end;
  gap: 24px;
  padding: 12px 16px;
  border-top: 1px solid ${({ theme }) => theme.colors.neutral20};
  .lbl {
    font-size: 15px;
    font-weight: ${FontWeights.Bold};
    color: ${({ theme }) => theme.getColor("gray", 1000)};
  }
  .val {
    min-width: 140px;
    text-align: right;
    font-size: 15px;
    font-weight: ${FontWeights.Bold};
    color: ${({ theme }) => theme.getColor("gray", 1000)};
    font-variant-numeric: tabular-nums;
    ${moneyFont}
  }
  .val .btw {
    font-size: 12px;
    font-weight: ${FontWeights.Regular};
    color: ${({ theme }) => theme.getColor("gray", 600)};
    margin-top: 2px;
  }
`;

export interface IUnitDistributionProps {
  // The pand, whose owner carries its vacancy and fixed owner share.
  propertyId: string;
  // The pand's name above its table, when the cost covers several.
  heading?: string;
  // The pand's part of the cost, excl. VAT.
  amount: number;
  contracts: ContractRef[];
  period: { from: string; to: string };
  // A shared cost: the tenants' share in %, the rest is the owner's.
  // Without it the tenants carry the whole amount.
  tenantSharePct?: number;
  vatRate: number;
  // How the tenants are billed, which decides the VAT on their rows (see
  // costVat.utils).
  billing: TCostBilling;
  // The VAT rate of our own a lease was billed at (see costVat.utils).
  outgoingVatPctByLease?: Record<string, number>;
  // "Subtotaal" under one pand of several, else "Totaal van de kost".
  isSubtotal?: boolean;
  // Leases that price the cost's kind as a forfait: never billed, their
  // share stays with the owner (decision 20; fixedFeeBilling.utils).
  fixedFeeLeaseIds?: Set<string>;
}

export const UnitDistribution: React.FC<IUnitDistributionProps> = ({
  propertyId,
  heading,
  amount,
  contracts,
  period,
  tenantSharePct,
  vatRate,
  billing,
  outgoingVatPctByLease = {},
  isSubtotal = false,
  fixedFeeLeaseIds,
}) => {
  const isShared = tenantSharePct != null;
  const res = prorateByOccupancy(
    amount,
    tenantSharePct ?? 100,
    contracts,
    period,
  );
  // Tenants + vacancy gaps, chronologically (oldest first).
  const rows = [
    ...res.shares.map(share => ({
      kind: "tenant" as const,
      isFixedFee: !!fixedFeeLeaseIds?.has(share.contractId),
      key: share.contractId,
      name: share.tenantName,
      fromIso: share.fromIso,
      toIso: share.toIso,
      days: share.days,
      amount: share.amount,
    })),
    ...res.vacancyIntervals.map((vacancy, index) => ({
      kind: "vacancy" as const,
      isFixedFee: false,
      key: `vac-${index}`,
      name: getLocalizedText("settlement.summary.vacancy"),
      fromIso: vacancy.fromIso,
      toIso: vacancy.toIso,
      days: vacancy.days,
      amount: vacancy.amount,
    })),
  ].sort((a, b) => a.fromIso.localeCompare(b.fromIso));
  const showOwnerBase =
    isShared && Math.abs(res.ownerBase) >= OWNER_BASE_THRESHOLD;
  // Each row's part of the pand's VAT: what the settlement or the tenant's
  // payment request bills it (see costVat.utils).
  const rowVat = costRowsVat({
    rows: [
      ...rows.map(row => {
        // A forfait tenant's share is the owner's (decision 20).
        const isTenant = row.kind === "tenant" && !row.isFixedFee;
        return {
          kind: isTenant ? ("tenant" as const) : ("owner" as const),
          partyId: isTenant ? row.key : propertyId,
          amount: row.amount,
          outgoingVatPct: isTenant ? outgoingVatPctByLease[row.key] : undefined,
        };
      }),
      {
        kind: "owner" as const,
        partyId: propertyId,
        amount: showOwnerBase ? res.ownerBase : 0,
      },
    ],
    vatPct: vatRate,
    billing,
  });

  return (
    <div>
      {heading && <UnitHeading>{heading}</UnitHeading>}
      <UnitDistTable>
        <DataTable
          columnContentTypes={["text", "text", "numeric"]}
          headings={[
            getLocalizedText("costs.modal.who_bears"),
            getLocalizedText("costs.field.temporal_basis"),
            getLocalizedText("costs.field.amount_excl_vat"),
          ]}
          rows={[
            ...rows.map((row, rowIndex) => ({
              id: row.key,
              content: [
                <div>
                  {row.kind === "vacancy" ? (
                    <TextStyle variation="subdued">{row.name}</TextStyle>
                  ) : (
                    row.name
                  )}
                  {row.isFixedFee && (
                    <div>
                      <TextStyle variation="subdued" size="small">
                        {getLocalizedText(
                          "settlement.distribution.owner_bears",
                        )}
                      </TextStyle>
                    </div>
                  )}
                  <div>
                    <TextStyle variation="subdued" size="small">
                      {costDayText(row.fromIso)} → {costDayText(row.toIso)}
                    </TextStyle>
                  </div>
                </div>,
                `${row.days} / ${res.periodDays}`,
                <MoneyWithVat
                  amount={row.amount}
                  vat={rowVat[rowIndex]}
                  variation="strong"
                />,
              ],
            })),
            ...(showOwnerBase
              ? [
                  {
                    id: "ownerbase",
                    content: [
                      <TextStyle variation="subdued">
                        {getLocalizedText(
                          "settlement.summary.owner_share_pct",
                          { pct: String(100 - (tenantSharePct ?? 0)) },
                        )}
                      </TextStyle>,
                      "—",
                      <MoneyWithVat
                        amount={res.ownerBase}
                        vat={rowVat[rows.length]}
                        variation="strong"
                      />,
                    ],
                  },
                ]
              : []),
          ]}
        />
        <DistTotal>
          <span className="lbl">
            {getLocalizedText(
              isSubtotal ? "costs.subtotal" : "costs.total_of_cost",
            )}
          </span>
          <span className="val">
            <Money amount={amount} size="inherit" />
            <div className="btw">
              {vatLineText(
                vatOfAmount({ amountExclVat: amount, vatPct: vatRate }),
              )}
            </div>
          </span>
        </DistTotal>
      </UnitDistTable>
    </div>
  );
};
